import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { EmailEditor, PlaceholderValidator, findUnknownPlaceholders } from '../common/EmailEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import api, { emailApi } from '../../lib/api';
import { ImportButton, ExportIconButton } from '../common/TemplateImportExport';
import { toast } from 'sonner';
import {
    Plus, Pencil, Trash2, Mail, Eye, Loader2, FileText, Send, AlertTriangle
} from 'lucide-react';

const TEMPLATE_TYPES = [
    { value: 'registration', label: 'E-Mail-Verifizierung', color: 'bg-red-50 text-red-700', trigger: 'Automatisch: Nach Registrierung' },
    { value: 'welcome', label: 'Willkommensbestätigung', color: 'bg-emerald-50 text-emerald-700', trigger: 'Automatisch: Nach Profil-Vervollständigung' },
    { value: 'confirmation', label: 'Anmeldebestätigung', color: 'bg-green-50 text-green-700', trigger: 'Automatisch: Nach Seminar-Buchung' },
    { value: 'booking_admin_notification', label: 'Neue Anmeldung (Geschäftsstelle)', color: 'bg-rose-50 text-rose-700', trigger: 'Automatisch: Nach jeder Seminar-Anmeldung — an die unter Systembenachrichtigungen hinterlegte Adresse' },
    { value: 'payment_request', label: 'Zahlungsaufforderung', color: 'bg-amber-50 text-amber-700', trigger: 'Manuell: Über Seminar-Verwaltung' },
    { value: 'payment_confirmation', label: 'Zahlungsbestätigung (Quittung)', color: 'bg-green-50 text-green-800', trigger: 'Automatisch: Nach Zahlungseingang (mit PDF-Beleg im Anhang)' },
    { value: 'meeting_link', label: 'Meeting-Link (Online/Hybrid)', color: 'bg-cyan-50 text-cyan-700', trigger: 'Automatisch: Nach Zahlungseingang bei Online-/Hybrid-Seminaren' },
    { value: 'password_reset', label: 'Passwort zurücksetzen', color: 'bg-orange-50 text-orange-700', trigger: 'Automatisch: Bei Passwort-vergessen-Anfrage' },
    { value: 'license', label: 'Lizenz-Versand', color: 'bg-blue-50 text-blue-700', trigger: 'Manuell: Über Lizenzverwaltung' },
    { value: 'renewal_due', label: 'Verlängerung fällig', color: 'bg-indigo-50 text-indigo-700', trigger: 'Automatisch: Wenn UE-Voraussetzungen erfüllt + Lizenz <6 Monate vor Ablauf' },
    { value: 'license_expiry_reminder', label: 'Lizenz-Ablauf-Frühwarnung', color: 'bg-rose-50 text-rose-700', trigger: 'Automatisch: 180 / 90 / 30 Tage vor Lizenzablauf (Stufen konfigurierbar unter Einstellungen → Lizenz-Erinnerungen)' },
    { value: 'certificate', label: 'Fortbildungsnachweis', color: 'bg-purple-50 text-purple-700', trigger: 'Manuell: Über Seminar-Verwaltung' },
    { value: 'participant_info', label: 'Teilnehmer: Info-Mail zum Kurs', color: 'bg-teal-50 text-teal-700', trigger: 'Manuell: „Mail an Teilnehmer" im Seminar' },
    { value: 'participant_thanks', label: 'Teilnehmer: Dankeschön + Bescheinigung', color: 'bg-teal-50 text-teal-700', trigger: 'Manuell: „Mail an Teilnehmer" im Seminar' },
    { value: 'participant_custom', label: 'Teilnehmer: Eigene Vorlage', color: 'bg-teal-50 text-teal-700', trigger: 'Manuell: „Mail an Teilnehmer" im Seminar' },
    { value: 'custom', label: 'Benutzerdefiniert', color: 'bg-gray-50 text-gray-700', trigger: 'Manuell' },
];

const CATEGORY_OPTIONS = [
    { value: 'system', label: 'System (automatisch/intern)' },
    { value: 'participant', label: 'Teilnehmer (manueller Mail-Versand)' },
];

const emptyForm = {
    name: '',
    subject: '',
    body_html: '',
    template_type: 'confirmation',
    description: '',
    category: 'system',
    survey_intro_html: '',
    is_active: true,
};

export default function EmailTemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);

    const handleSendTest = async () => {
        if (!(formData.subject || '').trim() || !(formData.body_html || '').trim()) {
            toast.error('Betreff und Inhalt sind Pflicht für die Testmail');
            return;
        }
        setSendingTest(true);
        try {
            const r = await emailApi.sendTest({
                subject: formData.subject,
                body_html: formData.body_html,
                include_survey_link: formData.category === 'participant',
                survey_intro_html: formData.survey_intro_html || '',
            });
            toast.success(r.data?.message || 'Testmail versendet');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Testmail fehlgeschlagen');
        } finally {
            setSendingTest(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    // Deep-Link-Support: ?type=license_expiry_reminder → öffnet das passende
    // Template direkt im Editor. Verwendet aus „Einstellungen → Lizenz-Erinnerungen".
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const wanted = searchParams.get('type');
        if (!wanted || templates.length === 0) return;
        const match = templates.find(t => t.template_type === wanted);
        if (match) {
            handleOpenDialog(match);
            // URL-Param entfernen, damit erneutes Schließen des Dialogs nicht wieder triggert
            searchParams.delete('type');
            setSearchParams(searchParams, { replace: true });
        } else {
            toast.error(`Vorlage „${wanted}" nicht gefunden`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates, searchParams]);

    const loadTemplates = async () => {
        try {
            const res = await api.get('/email-templates');
            setTemplates(res.data);
        } catch (e) {
            toast.error('Fehler beim Laden der Templates');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (template = null) => {
        if (template) {
            setSelectedTemplate(template);
            setFormData({
                name: template.name || '',
                subject: template.subject || '',
                body_html: template.body_html || '',
                template_type: template.template_type || 'custom',
                description: template.description || '',
                category: template.category || 'system',
                survey_intro_html: template.survey_intro_html || '',
                is_active: template.is_active !== false,
            });
        } else {
            setSelectedTemplate(null);
            setFormData(emptyForm);
        }
        setDialogOpen(true);
    };

    const [pendingSaveUnknown, setPendingSaveUnknown] = useState(null); // null oder Liste der unbekannten Keys

    const persistTemplate = async () => {
        setSaving(true);
        try {
            if (selectedTemplate) {
                await api.put(`/email-templates/${selectedTemplate.id}`, formData);
                toast.success('Template aktualisiert');
            } else {
                await api.post('/email-templates', formData);
                toast.success('Template erstellt');
            }
            setDialogOpen(false);
            setPendingSaveUnknown(null);
            loadTemplates();
        } catch (e) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.subject || !formData.body_html) {
            toast.error('Bitte Name, Betreff und Inhalt ausfüllen');
            return;
        }
        // Validator-Check: unbekannte Platzhalter → Bestätigungsdialog
        const unknown = findUnknownPlaceholders(
            formData.subject, formData.body_html, formData.survey_intro_html
        );
        if (unknown.length > 0) {
            setPendingSaveUnknown(unknown);
            return;
        }
        await persistTemplate();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Template wirklich löschen?')) return;
        try {
            await api.delete(`/email-templates/${id}`);
            toast.success('Template gelöscht');
            loadTemplates();
        } catch (e) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handlePreview = async (template) => {
        try {
            const res = await api.post(`/email-templates/${template.id}/preview`, {});
            setPreviewData(res.data);
            setPreviewOpen(true);
        } catch (e) {
            toast.error('Fehler bei der Vorschau');
        }
    };

    const getTypeBadge = (type) => {
        const t = TEMPLATE_TYPES.find(tt => tt.value === type) || TEMPLATE_TYPES[TEMPLATE_TYPES.length - 1];
        return (
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${t.color}`}>
                {t.label}
            </span>
        );
    };

    const getTriggerInfo = (type) => {
        const t = TEMPLATE_TYPES.find(tt => tt.value === type);
        if (!t || !t.trigger) return null;
        const isAuto = t.trigger.startsWith('Automatisch');
        return (
            <span className={`inline-flex items-center gap-1 text-xs ${isAuto ? 'text-blue-600' : 'text-gray-400'}`} data-testid={`trigger-info-${type}`}>
                <Send className="h-3 w-3" />
                {t.trigger}
            </span>
        );
    };

    return (
        <AdminLayout>
            <PageHeader
                title="E-Mail-Vorlagen"
                description="Verwalten Sie Ihre E-Mail-Templates für Bestätigungen, Zahlungsaufforderungen und mehr."
            />
            <div className="flex justify-end gap-2 mb-6">
                <ImportButton basePath="/email-templates" onImported={loadTemplates} testIdPrefix="email-tpl" />
                <Button onClick={() => handleOpenDialog()} data-testid="add-template-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Neue Vorlage
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : templates.length === 0 ? (
                <Card>
                    <CardContent className="p-12 text-center">
                        <Mail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Noch keine Vorlagen</h3>
                        <p className="text-gray-500 mb-6">Erstellen Sie Ihre erste E-Mail-Vorlage, um automatische und manuelle E-Mails zu versenden.</p>
                        <Button onClick={() => handleOpenDialog()} data-testid="add-first-template-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Erste Vorlage erstellen
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4" data-testid="templates-list">
                    {templates.map((t) => (
                        <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <h3 className="font-semibold text-gray-900">{t.name}</h3>
                                            {getTypeBadge(t.template_type)}
                                            {t.category === 'participant' && (
                                                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 text-teal-800 uppercase tracking-wide" data-testid={`category-badge-${t.id}`}>
                                                    Teilnehmer-Mail
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mb-1">
                                            <strong>Betreff:</strong> {t.subject}
                                        </p>
                                        {t.description && (
                                            <p className="text-sm text-gray-400">{t.description}</p>
                                        )}
                                        {getTriggerInfo(t.template_type)}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button variant="ghost" size="sm" onClick={() => handlePreview(t)} title="Vorschau" data-testid={`preview-template-${t.id}`}>
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(t)} title="Bearbeiten" data-testid={`edit-template-${t.id}`}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <ExportIconButton
                                            basePath="/email-templates"
                                            id={t.id}
                                            name={t.name}
                                            fileNamePrefix="bgpr-mailvorlage"
                                            testIdPrefix="email-tpl"
                                        />
                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} title="Löschen" className="text-red-600 hover:text-red-700" data-testid={`delete-template-${t.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Template Editor Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave}>
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name der Vorlage *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="z.B. Anmeldebestätigung Seminar"
                                        required
                                        data-testid="template-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="template_type">Typ *</Label>
                                    <Select
                                        value={formData.template_type}
                                        onValueChange={(v) => {
                                            // Auto-map: participant_* Typen => category=participant
                                            const auto = v.startsWith('participant_') ? 'participant' : formData.category;
                                            setFormData({ ...formData, template_type: v, category: auto });
                                        }}
                                    >
                                        <SelectTrigger data-testid="template-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TEMPLATE_TYPES.map((tt) => (
                                                <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="category">Kategorie *</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                                >
                                    <SelectTrigger data-testid="template-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORY_OPTIONS.map(c => (
                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    „Teilnehmer"-Vorlagen erscheinen im Mail-Composer auf der Seminar-Detailseite.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Beschreibung (intern)</Label>
                                <Input
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Kurzbeschreibung für die interne Zuordnung"
                                    data-testid="template-description"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="subject">Betreff *</Label>
                                <Input
                                    id="subject"
                                    value={formData.subject}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    placeholder="z.B. Ihre Anmeldung zu {{seminar_titel}}"
                                    required
                                    data-testid="template-subject"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Tipp: Verwenden Sie Platzhalter wie {'{{seminar_titel}}'} oder {'{{teilnehmer_name}}'}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Inhalt *</Label>
                                <EmailEditor
                                    content={formData.body_html}
                                    onChange={(html) => setFormData({ ...formData, body_html: html })}
                                />
                            </div>
                            <PlaceholderValidator texts={[formData.subject, formData.body_html, formData.survey_intro_html]} />
                            {formData.category === 'participant' && (
                                <div className="space-y-2 p-4 bg-teal-50/40 rounded-lg border border-teal-200/60">
                                    <Label className="flex items-center gap-2">
                                        <span>Umfrage-Intro-Text (optional)</span>
                                        <span className="text-[10px] uppercase tracking-wider bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Teilnehmer-Mail</span>
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        Dieser Text wird an der Stelle <code className="bg-gray-100 px-1">{'{{umfrage_block_html}}'}</code> eingefügt,
                                        wenn der Admin beim Versand den Umfrage-Toggle aktiviert und das Seminar einen Umfrage-Link hinterlegt hat.
                                        Wird hier nichts hinterlegt, zeigt die Mail einen neutralen Standard-Text.
                                    </p>
                                    <EmailEditor
                                        content={formData.survey_intro_html || ''}
                                        onChange={(html) => setFormData({ ...formData, survey_intro_html: html })}
                                        showPlaceholders={false}
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter className="gap-2 sm:gap-2">
                            <Button type="button" variant="outline" onClick={handleSendTest} disabled={sendingTest} data-testid="send-test-template-btn" title="Sendet eine Test-Mail mit Beispieldaten an Ihre Admin-E-Mail-Adresse">
                                {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                Testmail an mich
                            </Button>
                            <div className="flex-1" />
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="save-template-btn">
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                {selectedTemplate ? 'Aktualisieren' : 'Erstellen'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>E-Mail-Vorschau</DialogTitle>
                    </DialogHeader>
                    {previewData && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-gray-50 rounded-lg flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm"><strong>Betreff:</strong> {previewData.subject}</p>
                                </div>
                                <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${previewData.source === 'live-booking' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                    {previewData.source === 'live-booking' ? 'Echte Buchungsdaten' : 'Beispieldaten'}
                                </span>
                            </div>
                            {/* Iframe-based preview so we see the full mail incl. layout + signature 1:1 */}
                            <iframe
                                title="E-Mail-Vorschau"
                                srcDoc={previewData.wrapped_html || previewData.body_html}
                                className="w-full h-[500px] border border-gray-200 rounded-lg bg-[#f4f4f5]"
                                sandbox=""
                                data-testid="preview-iframe"
                            />
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                {previewData.source === 'live-booking'
                                    ? 'Vorschau basiert auf der zuletzt erstellten Buchung inkl. Mail-Abbinder.'
                                    : 'Keine Buchung vorhanden – Platzhalter werden mit Beispieldaten befüllt.'}
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Bestätigungs-Dialog: Unbekannte Platzhalter beim Speichern */}
            <Dialog open={pendingSaveUnknown !== null} onOpenChange={(v) => { if (!v) setPendingSaveUnknown(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                            Unbekannte Platzhalter im Text
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            Folgende Platzhalter werden vom Backend <strong>nicht</strong> automatisch ersetzt
                            und erscheinen wörtlich in der versendeten E-Mail:
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-testid="confirm-save-unknown-list">
                            {(pendingSaveUnknown || []).map((k) => (
                                <code key={k} className="text-xs font-mono bg-amber-50 border border-amber-300 text-amber-900 rounded px-1.5 py-0.5">
                                    {`{{${k}}}`}
                                </code>
                            ))}
                        </div>
                        <p className="text-muted-foreground text-xs">
                            Tipp: Häufiger Auslöser sind Tippfehler oder Reste aus alten Vorlagen. Sie können trotzdem speichern, falls Sie wissen, was Sie tun.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPendingSaveUnknown(null)} data-testid="confirm-save-cancel">
                            Zurück und korrigieren
                        </Button>
                        <Button onClick={persistTemplate} disabled={saving} data-testid="confirm-save-anyway">
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            Trotzdem speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
